import { useState, useMemo, type ReactNode } from 'react';
import { AuthContext } from './context';
import { DevBypassProvider } from './dev-bypass';
import { MsalAuthProvider } from './msal-provider';
import type { AuthContextValue } from './types';

/**
 * Runtime auth config. Each field falls back to its VITE_AZURE_* /
 * VITE_DEV_AUTH_BYPASS build-time env var when omitted — so local dev (Vite env)
 * keeps working unchanged, while a deployed env-agnostic bundle injects values at
 * runtime (e.g. from a deploy-written /config.js) by passing this prop.
 */
export interface AuthConfig {
  clientId?: string;
  tenantId?: string;
  apiScope?: string;
  devBypass?: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
  config?: AuthConfig;
}

const CONFIG_ERROR =
  'Set VITE_AZURE_CLIENT_ID, VITE_AZURE_TENANT_ID, and VITE_AZURE_API_SCOPE in your .env file, ' +
  'or set VITE_DEV_AUTH_BYPASS=true to skip auth in local dev.';

function MissingConfigProvider({ children }: { children: ReactNode }) {
  const [error, setError] = useState<string | null>(null);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: false,
      isLoading: false,
      error,
      account: null,
      signIn: async () => setError(CONFIG_ERROR),
      signOut: async () => {},
      getAccessToken: async () => {
        throw new Error('Not authenticated');
      },
    }),
    [error]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Top-level authentication provider. Selects a backend at render time.
 *
 * Config resolves per-field from the optional `config` prop, else the build-time
 * VITE_* env (so a deployed env-agnostic bundle injects values at runtime while
 * local dev keeps using its Vite env):
 * - dev bypass → {@link DevBypassProvider} (static stub, no network).
 * - clientId + tenantId + apiScope set → {@link MsalAuthProvider}.
 * - missing → `MissingConfigProvider` (shows a config error on sign-in).
 *
 * Wrap the entire app in this component exactly once (in `main.tsx`).
 */
export function AuthProvider({ children, config }: AuthProviderProps) {
  // `config.devBypass` can arrive at runtime (a deployed /config.js sets
  // window.__APP_CONFIG__), so gate it on the build mode too: Vite replaces
  // import.meta.env.DEV with `false` in a production build, which makes this
  // branch — and the DevBypassProvider import it guards — dead code that never
  // reaches the shipped bundle.
  const bypass =
    import.meta.env.DEV && (config?.devBypass ?? import.meta.env.VITE_DEV_AUTH_BYPASS === 'true');

  if (bypass) {
    return <DevBypassProvider>{children}</DevBypassProvider>;
  }

  const clientId = config?.clientId ?? (import.meta.env.VITE_AZURE_CLIENT_ID as string | undefined);
  const tenantId = config?.tenantId ?? (import.meta.env.VITE_AZURE_TENANT_ID as string | undefined);
  const apiScope = config?.apiScope ?? (import.meta.env.VITE_AZURE_API_SCOPE as string | undefined);

  if (!clientId || !tenantId || !apiScope) {
    return <MissingConfigProvider>{children}</MissingConfigProvider>;
  }

  return (
    <MsalAuthProvider clientId={clientId} tenantId={tenantId} apiScope={apiScope}>
      {children}
    </MsalAuthProvider>
  );
}
